import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { BaseAppointmentSlot } from '~/models/appointmentSlot'
import type {
  ActionRequest,
  BookingRequest,
  BookingVerificationNotification,
  RescheduleRequest,
  Notification as SoketiNotification,
} from '~/services/apiClient'
import {
  ActionStatus,
  ActionType,
  NotificationType,
} from '~/services/apiClient'

// Every event that carries an action request's changing status. The backend
// publishes one per request type (`notifications_repo._publish_action_request_update`)
// and they share a payload shape — miss one here and that request type's
// terminal states (rejected / failed) never reach the UI, leaving whatever
// surface started it stuck on an in-progress spinner.
const MUTATION_REQUEST_UPDATED_EVENTS: ReadonlySet<NotificationType> = new Set([
  NotificationType.BookingRequestUpdated,
  NotificationType.RescheduleRequestUpdated,
  NotificationType.EmptyRescheduleRequestUpdated,
])

// A rejected/failed reschedule already reflects its outcome — a later
// verification for the same `reschedule_request_id` is the backend's recovery
// re-booking of the *original* appointment (see the comment in
// `handleAppointmentVerificationEvent`), not confirmation of the reschedule.
const TERMINAL_FAILURE_STATUSES: ReadonlySet<BookingRequestStatus> = new Set([
  'rejected',
  'failed',
])

export const useMutationRequestsStore = defineStore('mutation-requests', () => {
  const mutationRequestStatesLookup = ref(
    new Map<string, MutationRequestState>()
  )
  const rescheduleRequestsByAppointmentID = computed(() => {
    const map = new Map<number, MutationRequestState>()
    for (const request of mutationRequestStatesLookup.value.values()) {
      if (
        (request.actionType === ActionType.Reschedule ||
          request.actionType === ActionType.RescheduleEmpty) &&
        request.originalAppointmentID // Should always be defined for reschedule
      ) {
        map.set(request.originalAppointmentID, request)
      }
    }
    return map
  })

  function handleAppointmentVerificationEvent(
    notification: SoketiNotification
  ) {
    if (notification.event_type !== NotificationType.BookingVerification) {
      throw new Error(`Invalid event type ${notification.event_type}`)
    }
    const verificationInformation =
      notification.object as BookingVerificationNotification
    const mutationRequest = verificationInformation.action_request
    const actionType = detectActionType(mutationRequest)

    // A cancel-and-rebook reschedule verifies its *booking* request, so the
    // payload is a BookingRequest whose only link back to the reschedule the
    // user started is `reschedule_request_id`. Follow it, exactly as the
    // request-updated handler does — without this the reschedule never leaves
    // `booked` and the row sits on a "Verifying..." spinner forever.
    //
    // Exception: when the reschedule's own attempt is rejected/failed, the
    // backend re-books the *original* appointment as recovery — a second,
    // unrelated BookingRequest that still carries the same
    // `reschedule_request_id` (only the reverse DB link is suppressed for it).
    // Its verification must not clobber the reschedule's already-terminal
    // status with "verified".
    let key = makeLookupKey(actionType, mutationRequest.id)
    if (mutationRequest.reschedule_request_id) {
      const rescheduleKey = makeLookupKey(
        ActionType.Reschedule,
        mutationRequest.reschedule_request_id
      )
      const rescheduleState =
        mutationRequestStatesLookup.value.get(rescheduleKey)
      if (
        !rescheduleState ||
        !TERMINAL_FAILURE_STATUSES.has(rescheduleState.status)
      ) {
        key = rescheduleKey
      }
    }
    const mutationRequestState = mutationRequestStatesLookup.value.get(key)
    if (!mutationRequestState) {
      console.warn(`No mutation request found for ${key}`)
      return
    }
    if (notification.level === 'success') {
      mutationRequestState.status = 'verified'
    } else {
      mutationRequestState.status = 'verification_issue'
    }
    mutationRequestState.appointmentID = mutationRequest.appointment_id
    mutationRequestState.latestMessage = `${notification.title}`
    if (notification.message) {
      mutationRequestState.latestMessage += `: ${notification.message}`
    }
    mutationRequestStatesLookup.value.set(key, mutationRequestState)
  }
  function handleMutationRequestUpdatedEvent(notification: SoketiNotification) {
    if (!MUTATION_REQUEST_UPDATED_EVENTS.has(notification.event_type)) {
      throw new Error(`Invalid event type ${notification.event_type}`)
    }
    // The event name does not identify the request type on its own: a
    // cancel-and-rebook reschedule reports its progress under
    // `BookingRequestUpdated` (carrying the booking request, remapped below),
    // while a true reschedule gets its own event. `action_type` on the payload
    // is the reliable discriminator, so route on that rather than the event.
    const mutationRequest = notification.object as
      RescheduleRequest | BookingRequest
    const requestID = mutationRequest.id
    const actionType = detectActionType(mutationRequest)
    let key = makeLookupKey(actionType, requestID)
    if (actionType === ActionType.Book) {
      const reschedule_request_id = (mutationRequest as BookingRequest)
        .reschedule_request_id
      if (reschedule_request_id) {
        // This is a "cancel-and-rebook" reschedule request, we should update the
        // reschedule request
        // NOTE: We do not send updates for the reschedule request for cancel-and-rebook
        // which is why we need this hack. Could consider sending reschedule updates,
        // but might be confusing to the user to receive both reschedule and booking
        // updates. One solution would be to make the reschedule notification have no
        // message or title to make it "silent"
        key = makeLookupKey(ActionType.Reschedule, reschedule_request_id)
      }
    }
    const mutationRequestState = mutationRequestStatesLookup.value.get(key)
    if (!mutationRequestState) {
      console.warn(`No mutation request found for '${key}'`)
      return
    }
    if (actionType === ActionType.Reschedule) {
      mutationRequestState.rescheduleRequest =
        mutationRequest as RescheduleRequest
    } else if (actionType === ActionType.Book) {
      mutationRequestState.bookingRequest = mutationRequest as BookingRequest
    }
    switch (mutationRequest.status) {
      case ActionStatus.Completed:
        mutationRequestState.status = 'booked'
        break
      case ActionStatus.Failed:
        mutationRequestState.status = 'failed'
        break
      case ActionStatus.Rejected:
        mutationRequestState.status = 'rejected'
        break
      default:
        console.warn('Unhandled booking request status', mutationRequest.status)
    }
    // Sometimes we get the appointment immediately
    // TODO: Is this right?
    mutationRequestState.appointmentID = mutationRequest.appointment_id
    mutationRequestState.latestMessage = `${notification.title}`
    if (notification.message) {
      mutationRequestState.latestMessage += `: ${notification.message}`
    }
  }
  function registerNewMutationRequest(state: MutationRequestState) {
    const key = makeLookupKeyFromState(state)
    mutationRequestStatesLookup.value.set(key, state)
  }

  function getLatestMutationRequestState(
    state: MutationRequestState
  ): MutationRequestState | undefined {
    return mutationRequestStatesLookup.value.get(makeLookupKeyFromState(state))
  }
  return {
    mutationRequestStatesLookup,
    rescheduleRequestsByAppointmentID,
    // This must be called first to allow us to then handle updates
    registerNewMutationRequest,
    handleMutationRequestUpdatedEvent,
    handleAppointmentVerificationEvent,
    getLatestMutationRequestState,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useMutationRequestsStore, import.meta.hot)
  )
}

export type BookingRequestStatus =
  | 'pending'
  | 'retrying'
  | 'rejected'
  | 'failed'
  | 'booked'
  | 'verified'
  | 'verification_issue'

export interface MutationRequestState {
  requestID: number
  actionType: ActionType
  selectedSlot: BaseAppointmentSlot
  originalAppointmentID: number | undefined // For reschedules
  status: BookingRequestStatus
  bookingRequest: BookingRequest | undefined
  rescheduleRequest: RescheduleRequest | undefined
  appointmentID: number | undefined
  latestMessage: string | undefined
}

function makeLookupKey(actionType: ActionType, requestID: number): string {
  return `${actionType}_${requestID}`
}

function makeLookupKeyFromState(state: MutationRequestState): string {
  return makeLookupKey(state.actionType, state.requestID)
}

export function detectActionType(
  mutationRequest: RescheduleRequest | BookingRequest | ActionRequest
): ActionType {
  if (mutationRequest.action_type === 'reschedule') {
    return ActionType.Reschedule
  } else if (mutationRequest.action_type === 'reschedule_empty') {
    return ActionType.RescheduleEmpty
  } else if (mutationRequest.action_type === 'book') {
    return ActionType.Book
  } else if (mutationRequest.action_type === 'book_empty') {
    return ActionType.BookEmpty
  } else {
    // While the typing says `.action_type` can be `undefined`, in reality it is always
    // filled out. The typing is a byproduct of how default arguments are interpreted
    // by the API client.
    throw new Error(`Unknown action type: ${mutationRequest.action_type}`)
  }
}
